import React, { useEffect, useState, useRef } from "react";
import { Loader2, CheckCircle2, AlertTriangle, ArrowRight } from "lucide-react";
import { fetchJsonWithRetry } from "../lib/fetchWithRetry";

interface VerifyEmailPageProps {
  onNavigate: (path: string) => void;
  onVerificationComplete?: () => void;
}

export function VerifyEmailPage({ onNavigate, onVerificationComplete }: VerifyEmailPageProps) {
  const [status, setStatus] = useState<"loading" | "success" | "error">("loading");
  const [errorMessage, setErrorMessage] = useState("");
  const verifyAttempted = useRef(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get("token") || params.get("token_hash");
    const type = params.get("type") || "signup";

    if (!token) {
      setStatus("error");
      setErrorMessage("No verification token was supplied in the URL.");
      return;
    }

    if (verifyAttempted.current) {
      return;
    }
    verifyAttempted.current = true;

    async function verify() {
      try {
        const data = await fetchJsonWithRetry(`/api/auth/verify-email?token=${encodeURIComponent(token || "")}&type=${encodeURIComponent(type || "signup")}`);

        if (data && data.success) {
          setStatus("success");
          if (onVerificationComplete) {
            onVerificationComplete();
          }
        } else {
          setStatus("error");
          setErrorMessage(data?.message || "Your verification link could not be validated.");
        }
      } catch (err: any) {
        setStatus("error");
        setErrorMessage(err.message || "A network error occurred while validating your token.");
      }
    }

    verify();
  }, []);

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-[#0a0f1d] text-slate-800 dark:text-slate-100 flex flex-col justify-center items-center p-6 relative overflow-hidden font-sans select-none" id="verify-email-page-container">
      {/* Decorative gradient glowing bubbles */}
      <div className="absolute top-1/4 left-1/4 w-[400px] h-[400px] bg-indigo-500/10 rounded-full blur-[100px] pointer-events-none" />
      <div className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] bg-emerald-500/10 rounded-full blur-[100px] pointer-events-none" />

      <div className="w-full max-w-md bg-white dark:bg-slate-900/60 backdrop-blur-xl border border-slate-200 dark:border-slate-800 rounded-2xl p-8 shadow-xl relative z-10 transition-all duration-300" id="verify-email-card">
        <div className="flex flex-col items-center text-center">
          
          {/* Logo / Title Accent */}
          <div className="flex items-center gap-2 mb-8" id="verify-email-logo">
            <span className="text-emerald-600 dark:text-emerald-400 font-extrabold text-2xl tracking-tight">BizNearYou</span>
            <span className="px-2 py-0.5 rounded text-[10px] uppercase font-mono font-bold tracking-widest bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20">
              Verified
            </span>
          </div>

          {status === "loading" && (
            <div className="py-8 flex flex-col items-center gap-4" id="verify-email-loading">
              <Loader2 className="w-12 h-12 text-emerald-500 dark:text-emerald-400 animate-spin" />
              <h2 className="text-xl font-bold text-slate-900 dark:text-white mt-2">Verifying your account...</h2>
              <p className="text-slate-600 dark:text-slate-400 text-sm max-w-xs">
                We are securely validating your unique registration token on the server. Please hold.
              </p>
            </div>
          )}

          {status === "success" && (
            <div className="py-4 flex flex-col items-center" id="verify-email-success">
              <div className="w-16 h-16 bg-emerald-500/10 border border-emerald-500/30 rounded-full flex justify-center items-center mb-6 animate-pulse">
                <CheckCircle2 className="w-8 h-8 text-emerald-400" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-3">Email Verified!</h2>
              <p className="text-slate-400 text-sm mb-8 leading-relaxed max-w-sm">
                Your email has been verified. You can now use BizNearYou’s business search tools.
              </p>
              <button
                id="btn-verify-success-go-dashboard"
                onClick={() => onNavigate("/dashboard")}
                className="w-full py-3 px-6 bg-blue-600 hover:bg-blue-700 dark:bg-slate-800 dark:hover:bg-slate-700 text-white font-semibold rounded-lg flex items-center justify-center gap-2 transition-colors cursor-pointer group shadow-sm"
              >
                Go to Dashboard
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </button>
            </div>
          )}

          {status === "error" && (
            <div className="py-4 flex flex-col items-center" id="verify-email-error">
              <div className="w-16 h-16 bg-rose-500/10 border border-rose-500/30 rounded-full flex justify-center items-center mb-6">
                <AlertTriangle className="w-8 h-8 text-rose-400" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-3">Verification Failed</h2>
              <p className="text-slate-400 text-sm mb-8 leading-relaxed max-w-sm">
                {errorMessage || "This email verification link is invalid, expired, or has already been used."}
              </p>
              <div className="w-full flex flex-col gap-3">
                <button
                  id="btn-verify-error-go-login"
                  onClick={() => onNavigate("/login")}
                  className="w-full py-3 px-6 bg-slate-800 hover:bg-slate-700 text-white font-semibold rounded-lg transition-colors cursor-pointer flex items-center justify-center gap-2"
                >
                  Return to Log-In
                </button>
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}
